import { useEffect, useMemo, useState } from 'react';
import type {
  PopupMenuAction,
  PopupMenuEntry,
  PopupMenuSection,
} from '../types.ts';

type MobileNavigationLevel = {
  id: string;
  label: string;
  actions: PopupMenuAction[];
};

export function useNestedNavigation({
  entry,
  isDesktopLargeDesktop,
  isOpen,
}: {
  entry: PopupMenuEntry;
  isDesktopLargeDesktop: boolean;
  isOpen: boolean;
}) {
  const [mobileNavigationStack, setMobileNavigationStack] = useState<
    MobileNavigationLevel[]
  >([]);
  const [desktopExpandedPath, setDesktopExpandedPath] = useState<string[]>([]);

  useEffect(() => {
    if (isOpen) return;

    setMobileNavigationStack([]);
    setDesktopExpandedPath([]);
  }, [isOpen]);

  useEffect(() => {
    setMobileNavigationStack([]);
    setDesktopExpandedPath([]);
  }, [entry.id]);

  const visibleSections = useMemo((): PopupMenuSection[] => {
    if (isDesktopLargeDesktop) return entry.sections;

    const currentLevel =
      mobileNavigationStack[mobileNavigationStack.length - 1];

    if (!currentLevel) return entry.sections;

    return [
      {
        id: currentLevel.id,
        label: currentLevel.label,
        actions: currentLevel.actions,
      },
    ];
  }, [entry.sections, isDesktopLargeDesktop, mobileNavigationStack]);

  const currentMobileLevelLabel =
    mobileNavigationStack[mobileNavigationStack.length - 1]?.label;

  const canGoBack = !isDesktopLargeDesktop && mobileNavigationStack.length > 0;

  const goBack = (): void => {
    if (!canGoBack) return;

    setMobileNavigationStack((previousStack) => previousStack.slice(0, -1));
  };

  const resetNestedNavigation = (): void => {
    setMobileNavigationStack([]);
    setDesktopExpandedPath([]);
  };

  const handleActionSelectionIntent = ({
    action,
    path,
  }: {
    action: PopupMenuAction;
    path: string[];
  }): boolean => {
    const hasChildren = Boolean(action.children?.length);

    if (!hasChildren) return false;

    if (isDesktopLargeDesktop) {
      setDesktopExpandedPath(path);
      return true;
    }

    setMobileNavigationStack((previousStack) => {
      const nextLevel: MobileNavigationLevel = {
        id: `${action.id}-children`,
        label: action.label,
        actions: action.children || [],
      };

      return [...previousStack, nextLevel];
    });

    return true;
  };

  const handleDesktopActionHover = ({
    action,
    path,
  }: {
    action: PopupMenuAction;
    path: string[];
  }): void => {
    if (!isDesktopLargeDesktop) return;
    if (!action.children?.length) return;

    setDesktopExpandedPath(path);
  };

  const isDesktopActionExpanded = (path: string[]): boolean => {
    if (!isDesktopLargeDesktop) return false;
    if (path.length === 0) return false;
    if (desktopExpandedPath.length < path.length) return false;

    return path.every(
      (pathPart, index) => desktopExpandedPath[index] === pathPart
    );
  };

  return {
    canGoBack,
    currentMobileLevelLabel,
    goBack,
    handleActionSelectionIntent,
    handleDesktopActionHover,
    isDesktopActionExpanded,
    resetNestedNavigation,
    visibleSections,
  };
}
